import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { IntercomTeammate } from '@/services/intercom-integration.service';
import { useIntercomKimTeammate } from '../hooks/use-intercom-kim-teammate';

const TEAMMATE_FIELD_ID = 'intercom-kim-teammate';

const Hint = ({ children }: { children: string }) => (
	<p className='text-xs text-muted-foreground leading-snug'>{children}</p>
);

const teammateLabel = ({ name, email }: IntercomTeammate) => (name === email ? email : `${name} · ${email}`);

/**
 * The second step of connecting Intercom: which teammate of the workspace Kim works as. Kim
 * assigns the conversations it is working on to that teammate, which is how Sentinel tells its
 * own conversations from the team's.
 */
export const IntercomKimTeammate = () => {
	const { teammates, savedTeammateId, isLoading, loadFailed, isSaving, errorMessage, save } = useIntercomKimTeammate();
	// What the operator picked but has not saved yet; null until they touch the list.
	const [pickedTeammateId, setPickedTeammateId] = useState<string | null>(null);
	const teammateId = pickedTeammateId ?? savedTeammateId ?? '';
	const isSaved = teammateId !== '' && teammateId === savedTeammateId;

	return (
		<section aria-label='Teammate for Kim' className='flex flex-col gap-3 rounded-lg border p-3'>
			<div className='flex flex-col gap-1'>
				<h4 className='text-sm font-medium'>
					{savedTeammateId ? 'Teammate for Kim' : 'Step 2: Choose a teammate for Kim'}
				</h4>
				<Hint>
					Kim assigns the conversations it is working on to this teammate, so they stay apart from your team's. Pick
					one nobody on your team works from.
				</Hint>
			</div>

			{isLoading && <Hint>Loading teammates…</Hint>}
			{loadFailed && <Hint>We could not load the teammates from Intercom. Reopen this panel to try again.</Hint>}
			{!isLoading && !loadFailed && teammates.length === 0 && (
				<Hint>No teammate with an email address was found in this workspace.</Hint>
			)}

			{teammates.length > 0 && (
				<>
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor={TEAMMATE_FIELD_ID} className='text-xs text-muted-foreground'>
							Teammate
						</Label>
						<Select value={teammateId} onValueChange={setPickedTeammateId} disabled={isSaving}>
							<SelectTrigger id={TEAMMATE_FIELD_ID} className='w-full cursor-pointer'>
								<SelectValue placeholder='Select a teammate' />
							</SelectTrigger>
							<SelectContent>
								{teammates.map((teammate) => (
									<SelectItem key={teammate.id} value={teammate.id} className='cursor-pointer'>
										{teammateLabel(teammate)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{errorMessage && (
						<div role='alert' className='flex items-start gap-2 rounded-lg border p-3 text-destructive'>
							<AlertTriangle className='mt-0.5 size-4 shrink-0' />
							<span className='text-sm'>{errorMessage}</span>
						</div>
					)}

					<Button
						type='button'
						className='w-full'
						disabled={teammateId === '' || isSaved || isSaving}
						onClick={() => void save(teammateId)}
					>
						{isSaving && <Loader2 className='size-4 animate-spin' />}
						{isSaving ? 'Saving…' : isSaved ? 'Saved' : 'Save teammate'}
					</Button>
				</>
			)}
		</section>
	);
};
